import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { PeriodoActivoService } from '../../../proyecto/service/periodo-activo/periodo-activo.service';

export interface PeriodOption {
  mes: number;
  anio: number;
  label: string;
}

export interface PeriodValue {
  mes: number;
  anio: number;
}

const MONTH_OFFSET_START = 3;
const MONTH_OFFSET_END = 14;

export function buildPeriodOptions(): PeriodOption[] {
  const currentYear = new Date().getFullYear();
  const count = MONTH_OFFSET_END + MONTH_OFFSET_START + 1;
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(currentYear, -MONTH_OFFSET_START + i, 1);
    const mes = date.getMonth() + 1;
    const anio = date.getFullYear();
    const raw = date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    return { mes, anio, label: raw.charAt(0).toUpperCase() + raw.slice(1) };
  }).reverse();
}

@Component({
  selector: 'app-periodo-select',
  templateUrl: './periodo-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeriodoSelectComponent),
      multi: true,
    },
  ],
})
export class PeriodoSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  readonly label = input<string>('Período');
  readonly allowNull = input<boolean>(false);
  readonly appearance = input<'outline' | 'fill'>('outline');

  private readonly periodoActivoService = inject(PeriodoActivoService);
  private readonly destroy$ = new Subject<void>();

  readonly allPeriods: PeriodOption[] = buildPeriodOptions();
  readonly filteredPeriods = signal<PeriodOption[]>(this.allPeriods);

  readonly searchControl = new FormControl<string>('');
  readonly selectedControl = new FormControl<PeriodOption | null>(null);

  private skipApiPreselect = false;
  private onChange: (value: PeriodValue | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      startWith(''),
      map(term => this.filterPeriods(term)),
      takeUntil(this.destroy$),
    ).subscribe(filtered => this.filteredPeriods.set(filtered));

    this.selectedControl.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(value => {
      this.onChange(value ? { mes: value.mes, anio: value.anio } : null);
      this.onTouched();
    });

    if (!this.skipApiPreselect && !this.selectedControl.value) {
      this.periodoActivoService.getPeriodoActivo$().pipe(
        takeUntil(this.destroy$),
      ).subscribe({
        next: (periodo) => {
          if (!this.selectedControl.value) {
            const match = this.allPeriods.find(p => p.mes === periodo.mes && p.anio === periodo.anio);
            if (match) {
              this.selectedControl.setValue(match, { emitEvent: true });
            }
          }
        },
        error: () => { /* fail silently — user can select manually */ },
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  readonly comparePeriods = (a: PeriodOption | null, b: PeriodOption | null): boolean => {
    if (!a || !b) return a === b;
    return a.mes === b.mes && a.anio === b.anio;
  };

  private filterPeriods(term: string | null): PeriodOption[] {
    if (!term) return this.allPeriods;
    const lower = term.toLowerCase();
    return this.allPeriods.filter(p => p.label.toLowerCase().includes(lower));
  }

  // ControlValueAccessor implementation
  writeValue(value: PeriodValue | null): void {
    if (value != null) {
      this.skipApiPreselect = true;
      const match = this.allPeriods.find(p => p.mes === value.mes && p.anio === value.anio);
      this.selectedControl.setValue(match ?? null, { emitEvent: false });
    } else {
      this.selectedControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: PeriodValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedControl.disable({ emitEvent: false });
    } else {
      this.selectedControl.enable({ emitEvent: false });
    }
  }
}
